import React, { useRef } from "react";

import Box from "@mui/material/Box";
import {
  Alert,
  Card,
  Stack,
  TextField,
  Typography,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Bounce, toast } from "react-toastify";
import * as Yup from "yup";
import { Formik } from "formik";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import { organizationsService } from "./services";
import CustomButton from "../common/Button";
import Headerbar from "../common/Headerbar";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Box>
  );
}

export default function AccountView() {
  const [tabValue, setTablValue] = React.useState(0);

  const sidebarRef = useRef<SidebarRef>(null);
  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTablValue(newValue);
  };

  const { data, isSuccess } = useQuery({
    queryKey: ["organization"],
    queryFn: () => organizationsService.fetchOrganizationDetails(),
  });

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationKey: ["update-project"],
    mutationFn: (value: string) =>
      organizationsService.updateOrganization(value),
  });

  const handleSave = async (params: { organization_name: string }) => {
    await toast.promise(mutateAsync(params.organization_name), {
      success: {
        toastId: `${params.organization_name}-success`,
        render() {
          return "Updated!";
        },
        type: "success",
        theme: "colored",
        autoClose: 2000,
        transition: Bounce,
        delay: 500,
      },
    });
  };

  return (
    <>
      <SidebarView ref={sidebarRef} pageName="Account Settings" />
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
        }}
      >
        <Headerbar
          pageName="Account Settings"
          onMobileMenuClick={handleMobileMenuToggle}
        />
        <Card
          component={Stack}
          spacing={0}
          direction="column"
          sx={{
            position: "relative",
            px: 4,
            py: 4,
            borderRadius: 0,
            border: "none",
            backgroundColor: "transparent",
            boxShadow: "none",
            margin: 12,
            marginLeft: 6,
            marginRight: 6,
          }}
        >
          {/* Modern tabs container */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: 4,
              boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
              mb: 4,
              overflow: "hidden",
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleChange}
                aria-label="account settings tabs"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontSize: 16,
                    fontWeight: 600,
                    minHeight: 64,
                    color: "#666",
                    "&.Mui-selected": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1976d2",
                    height: 3,
                  },
                }}
              >
                <Tab
                  icon={<BusinessIcon sx={{ fontSize: 24, mb: 0.5 }} />}
                  iconPosition="start"
                  label="Organization"
                  sx={{ px: 4 }}
                />
              </Tabs>
            </Box>
          </Paper>

          {/* Content container */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: 4,
              boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
              overflow: "hidden",
            }}
          >
            <CustomTabPanel value={tabValue} index={0}>
              <Box sx={{ maxWidth: 600, mx: "auto" }}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <BusinessIcon
                    sx={{
                      fontSize: 48,
                      color: "#1976d2",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 1,
                      color: "#23272E",
                      fontWeight: 600,
                    }}
                  >
                    Organization Settings
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#666",
                    }}
                  >
                    Manage your organization information and settings
                  </Typography>
                </Box>

                {isSuccess && (
                  <Formik
                    initialValues={{ organization_name: data?.name ?? "" }}
                    validationSchema={Yup.object({
                      organization_name: Yup.string().required(
                        "Organization name is required",
                      ),
                    })}
                    onSubmit={handleSave}
                  >
                    {(formik) => (
                      <form onSubmit={formik.handleSubmit}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          {isError ? (
                            <Alert
                              severity="error"
                              sx={{
                                borderRadius: 3,
                                "& .MuiAlert-message": {
                                  fontSize: 14,
                                  fontWeight: 500,
                                },
                              }}
                            >
                              {error.message}
                            </Alert>
                          ) : null}

                          <TextField
                            label="Organization Name"
                            error={!!formik.errors.organization_name}
                            helperText={formik.errors.organization_name}
                            {...formik.getFieldProps("organization_name")}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                                fontSize: 16,
                                backgroundColor: "#fff",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#1976d2",
                                    borderWidth: 2,
                                  },
                                "&.Mui-error .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#d32f2f",
                                  },
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: 16,
                                fontWeight: 500,
                                color: "#666",
                                "&.Mui-focused": {
                                  color: "#1976d2",
                                  fontWeight: 600,
                                },
                                "&.Mui-error": {
                                  color: "#d32f2f",
                                },
                              },
                              "& .MuiFormHelperText-root": {
                                fontSize: 14,
                                marginLeft: 0,
                                "&.Mui-error": {
                                  color: "#d32f2f",
                                },
                              },
                            }}
                          />

                          <CustomButton
                            label="Update Organization"
                            type="submit"
                            variant="contained"
                            disabled={isPending}
                            sx={{
                              borderRadius: 999,
                              px: 3,
                              py: 1,
                              fontSize: 14,
                              textTransform: "none",
                              fontWeight: 600,
                              letterSpacing: 0.2,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              alignSelf: "center",
                              minWidth: 200,
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                              },
                            }}
                          />
                        </Box>
                      </form>
                    )}
                  </Formik>
                )}
              </Box>
            </CustomTabPanel>
          </Paper>
        </Card>
      </Box>
    </>
  );
}
